"use client";

import React, { ReactNode, createContext, forwardRef, useContext, useState } from "react";
import { AnimatePresence, MotionConfig, motion } from "framer-motion";
import ReactPlayer from "react-player/lazy";
import useMeasure from "react-use-measure";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import SidePanel from "@components/ui/side-panel";
import { useTranslation } from "next-i18next";
import { getCurrentLocationAndLanguage } from '@utils/location';
type ThemeContextType = {
  panelClass?: string;
};
const ThemeContext = createContext<ThemeContextType>({});
const useCustomMeasure = () => {
  const [ref, bounds] = useMeasure();
  const animateProps = {
    animate: {
      height: bounds.height > 0 ? bounds.height : 0.1
    },
    transition: {
      type: "spring",
      bounce: 0.02,
      duration: 0.65
    }
  };
  return {
    ref,
    animateProps
  };
};
type ResizablePanelProps = {
  children: ReactNode;
  className?: string;
};
const ResizablePanel = forwardRef<HTMLDivElement, ResizablePanelProps>(({
  children,
  className,
  ...props
}, ref) => {
  const transition = {
    type: "ease",
    ease: "easeInOut",
    duration: 0.4
  };
  return <MotionConfig transition={transition}>
        <div ref={ref} className={cn("flex w-full flex-col items-start", className)} {...props}>
          <div className="mx-auto w-full">
            <div className={cn(children ? "rounded-r-none" : "rounded-sm", "relative overflow-hidden")}>
              {children}
            </div>
          </div>
        </div>
      </MotionConfig>;
});
ResizablePanel.displayName = "ResizablePanel";
type VideoPowerButtonProps = {
  handleVideoOpen: () => void;
};
const VideoPowerButton = forwardRef<HTMLInputElement, VideoPowerButtonProps>(({
  handleVideoOpen
}, ref) => {
  return <div className="flex flex-col items-center py-2 mx-3">
        <input ref={ref} className="pb-4" id="checkbox" type="checkbox" onChange={handleVideoOpen} />
        <label className="switch" htmlFor="checkbox">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className="slider">
          </svg>
        </label>
      </div>;
});
VideoPowerButton.displayName = "VideoPowerButton";
type YoutubeVideoProps = {
  videoOpen: boolean;
  url: string;
};
const YoutubeVideo = forwardRef<HTMLDivElement, YoutubeVideoProps>(({
  videoOpen,
  url
}, ref) => {
  return <AnimatePresence>
        {videoOpen && <motion.div ref={ref} className="md:flex md:justify-center py-1 px-1 md:py-8 md:px-8 w-full h-[300px] md:h-[800px] md:aspect-video rounded-2xl bg-black" initial="hidden" animate="visible" exit="hidden">
            <ReactPlayer width="100%" height="100%" controls={false} color="white" url={url} />
          </motion.div>}
      </AnimatePresence>;
});
YoutubeVideo.displayName = "YoutubeVideo";
type VideoContentProps = {
  url: string;
  videoOpen: boolean;
};
const VideoContent: React.FC<VideoContentProps> = ({
  url,
  videoOpen
}) => {
  const videoVariants = {
    hidden: {
      opacity: 0,
      scale: 0.9,
      y: 30
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0
    }
  };
  const transition = {
    duration: 0.2,
    ease: [0.04, 0.62, 0.23, 0.98],
    delay: 0.3
  };
  return <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-component="VideoContent" data-sentry-source-file="why-forever-cta.tsx">
      {videoOpen && <motion.div className="md:flex md:justify-center py-1 px-1 md:py-8 md:px-8 w-full h-[300px] md:h-[800px] md:aspect-video rounded-2xl bg-black" initial="hidden" animate="visible" exit="hidden" variants={videoVariants} transition={transition}>
          <ReactPlayer width="100%" height="100%" controls={false} color="white" url={url} />
        </motion.div>}
    </AnimatePresence>;
};
type VideoSectionProps = {
  videoOpen: boolean;
  handleVideoOpen: () => void;
  className?: string;
  videoUrl: string;
  children?: ReactNode;
};
const VideoSection: React.FC<VideoSectionProps> = ({
  videoOpen,
  handleVideoOpen,
  className,
  videoUrl,
  children
}) => {
  const theme = useContext(ThemeContext);
  return <ResizablePanel className={cn(className, theme.panelClass)} data-sentry-element="ResizablePanel" data-sentry-component="VideoSection" data-sentry-source-file="why-forever-cta.tsx">
      {children ? children : <>
          <VideoPowerButton handleVideoOpen={handleVideoOpen} />
          <VideoContent url={videoUrl} videoOpen={videoOpen} />
        </>}
    </ResizablePanel>;
};
export { VideoContent, VideoSection, YoutubeVideo, VideoPowerButton, ResizablePanel, useCustomMeasure };
export function SidePanelVideos() {
  const {
    t
  } = useTranslation('common');
  const {
    location
  } = getCurrentLocationAndLanguage();
  const videoUrl = ['kg', 'kz', 'uz'].includes(location) ? "https://youtu.be/AjE8yt1OObU?si=T6gjnut8598-Rk0Y" : "https://www.youtube.com/watch?v=Gi_VMpTxWq8";
  const [videoOpen, setVideoOpen] = useState(false);
  const handleVideoOpen = () => {
    setVideoOpen(!videoOpen);
  };
  const renderVideoButton = () => <div className={cn("flex items-center w-full justify-start pr-4 md:pl-4 py-1 md:py-1", videoOpen ? "pr-3" : "")} data-sentry-component="renderVideoButton" data-sentry-source-file="why-forever-cta.tsx">
      <p className="text-xl font-black tracking-tight text-gray-900 sm:text-3xl">
        <span className="bg-gradient-to-t from-neutral-200 to-stone-300 bg-clip-text font-brand text-xl font-bold text-transparent sm:text-4xl">
          video
        </span>
      </p>
      <Button className="rounded-r-[33px] py-8 ml-2 " onClick={handleVideoOpen} variant="secondary" data-sentry-element="Button" data-sentry-source-file="why-forever-cta.tsx">
        {videoOpen ? t('common:text-close') : t('common:button-view')}
      </Button>
    </div>;
  return <div className="z-10 w-full" data-sentry-component="SidePanelVideos" data-sentry-source-file="why-forever-cta.tsx">
      <div className="mt-4">
        <SidePanel panelOpen={videoOpen} handlePanelOpen={handleVideoOpen} renderButton={renderVideoButton} data-sentry-element="SidePanel" data-sentry-source-file="why-forever-cta.tsx">
          <YoutubeVideo videoOpen={videoOpen} url={videoUrl} data-sentry-element="YoutubeVideo" data-sentry-source-file="why-forever-cta.tsx" />
        </SidePanel>
      </div>
    </div>;
}
export default function CallToAction() {
  const {
    t
  } = useTranslation('common');
  return <section className="bg-neutral-100 dark:bg-neutral-800 rounded-lg shadow-md" data-sentry-component="CallToAction" data-sentry-source-file="why-forever-cta.tsx">
      <div className="py-8 px-4 mx-auto sm:py-16 lg:px-6">
          <h2 className="mb-4 text-2xl tracking-tight font-extrabold text-neutral-900 dark:text-white">
          {t('common:text-whyforever-cta-title')}
          </h2>
          <p className="mb-8 text-neutral-500 text-sm dark:text-neutral-400">
             {t('common:text-whyforever-cta-description')}
          </p>
          <div className="flex flex-col space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4">
          <SidePanelVideos data-sentry-element="SidePanelVideos" data-sentry-source-file="why-forever-cta.tsx" />
          </div>
        </div>
    </section>;
}